import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { Button } from '@trello/nachos/button';
import type { PopoverProps } from '@trello/nachos/popover';
import { Popover } from '@trello/nachos/popover';

import { useBoardHeaderUnsubscribeMutation } from './BoardHeaderUnsubscribeMutation.generated';

import * as styles from './BoardHeaderSubscribedButtonPopover.module.less';

interface BoardHeaderSubscribedButtonPopoverProps {
  boardId: string;
  popoverProps: PopoverProps<HTMLButtonElement, HTMLElement>;
}

export const BoardHeaderSubscribedButtonPopover: FunctionComponent<
  BoardHeaderSubscribedButtonPopoverProps
> = ({ boardId, popoverProps }) => {
  const [unsubscribe] = useBoardHeaderUnsubscribeMutation();

  const onClickConfirm = useCallback(async () => {
    const traceId = Analytics.startTask({
      taskName: 'edit-board/subscribed',
      source: 'boardScreen',
    });

    try {
      await unsubscribe({
        variables: { boardId, board: { subscribed: false }, traceId },
        optimisticResponse: {
          __typename: 'Mutation',
          updateBoard: {
            __typename: 'Board',
            id: boardId,
            subscribed: false,
          },
        },
      });

      Analytics.taskSucceeded({
        taskName: 'edit-board/subscribed',
        traceId,
        source: 'boardScreen',
      });
      Analytics.sendTrackEvent({
        action: 'unsubscribed',
        actionSubject: 'board',
        source: 'boardScreen',
        containers: {
          board: {
            id: boardId,
          },
        },
        attributes: {
          taskId: traceId,
        },
      });
    } catch (error) {
      Analytics.taskFailed({
        taskName: 'edit-board/subscribed',
        traceId,
        source: 'boardScreen',
        error,
      });
    }
  }, [boardId, unsubscribe]);

  return (
    <Popover
      title={
        <FormattedMessage
          id="templates.board_header.stop-watching-title"
          defaultMessage="Stop watching?"
          description="The title of the popover for unsubscribing from a board"
        />
      }
      {...popoverProps}
    >
      <p>
        <FormattedMessage
          id="templates.board_header.you-can-watch-again"
          defaultMessage="You can watch again via the board menu."
          description="The content of the popover for unsubscribing from a board"
        />
      </p>
      <Button
        className={styles.unsubscribeConfirmButton}
        appearance="danger"
        shouldFitContainer
        onClick={onClickConfirm}
      >
        <FormattedMessage
          id="templates.board_header.stop-watching-confirm"
          defaultMessage="Stop watching"
          description="The confirm text for unsubscribing from a board"
        />
      </Button>
    </Popover>
  );
};
